// Financial Reporting tab — the loan's tax-completion date.
//
// This is not a passive fact. An Annual request's due date is this date + 30
// days, so until it is recorded the Annual deadline would be a guess and the
// server refuses to issue one (decision 23). The panel says that out loud,
// because otherwise "Annual is blocked" and "somebody has not typed a date"
// look like two unrelated problems.
//
// The date is a WALL-CALENDAR value: rendered with formatCalendarDate (UTC)
// and written back through calendarDateInputToIso, never the tz-aware
// formatter — at a negative UTC offset that would render the previous day, and
// on the 1st the previous month.

import { useEffect, useState } from "react";
import { Button, Card, Input, useToast } from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { calendarDateInputToIso, formatCalendarDate } from "@/design-system/lib/format";
import { useRecordTaxesCompleted } from "@/hooks/portfolioAdmin/useReportRequests";

interface TaxCompletionPanelProps {
  loanId: string;
  taxesCompletedAt: string | null;
  recordedByUserName: string | null;
  /** Reporting.ReviewRequests — the same gate the endpoint carries. */
  canEdit: boolean;
}

/** The id the Annual block's "Record the date" door focuses. Exported so the
 *  door and the field can never drift apart into a control that scrolls to
 *  nothing. */
export const TAX_COMPLETION_INPUT_ID = "taxes-completed-at";

/** ISO instant → the `YYYY-MM-DD` an `<input type="date">` wants, read in UTC
 *  so a wall-calendar date does not shift a day on the way into the field. */
function toDateInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export function TaxCompletionPanel({
  loanId,
  taxesCompletedAt,
  recordedByUserName,
  canEdit,
}: TaxCompletionPanelProps) {
  const toast = useToast();
  const record = useRecordTaxesCompleted(loanId);
  const [value, setValue] = useState(() => toDateInputValue(taxesCompletedAt));

  // Re-sync when the overview refetches (another reviewer recorded a date, or
  // this panel's own save landed).
  useEffect(() => {
    setValue(toDateInputValue(taxesCompletedAt));
  }, [taxesCompletedAt]);

  const stored = toDateInputValue(taxesCompletedAt);
  const dirty = value !== stored;

  const save = (next: string | null) => {
    record.mutate(next, {
      onSuccess: () =>
        toast.success(
          next ? "Tax-completion date recorded." : "Tax-completion date cleared.",
        ),
      onError: (err) => toast.error(extractErrorMessage(err)),
    });
  };

  return (
    <Card data-testid="tax-completion-panel">
      <h3 className="text-body font-semibold text-onyx m-0">Tax-completion date</h3>
      <p className="text-caption text-gray-mid m-0 mt-space-1 max-w-prose">
        The date the borrower&apos;s tax work was completed. An Annual request is
        due 30 days after it, so an Annual request <strong>cannot be issued</strong>{" "}
        until this date is recorded — the deadline is computed from it, not from
        the reporting period.
      </p>

      {taxesCompletedAt ? (
        <p className="text-small text-onyx m-0 mt-space-2" data-testid="tax-completion-current">
          Recorded: <strong>{formatCalendarDate(taxesCompletedAt)}</strong>
          {recordedByUserName ? (
            <span className="text-gray-mid"> · by {recordedByUserName}</span>
          ) : null}
        </p>
      ) : (
        <p
          className="text-small text-status-warning-ink m-0 mt-space-2"
          data-testid="tax-completion-missing"
        >
          Not recorded — Annual requests are blocked until it is.
        </p>
      )}

      {canEdit && (
        <div className="mt-space-3 flex flex-wrap items-end gap-space-3">
          <div className="min-w-[12rem]">
            <label
              className="block text-small font-medium text-onyx mb-space-1"
              htmlFor={TAX_COMPLETION_INPUT_ID}
            >
              Tax work completed on
            </label>
            <Input
              id={TAX_COMPLETION_INPUT_ID}
              type="date"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="tax-completion-input"
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty || value === "" || record.isPending}
            loading={record.isPending}
            onClick={() => save(calendarDateInputToIso(value))}
            data-testid="tax-completion-save"
          >
            Save date
          </Button>
          {stored !== "" && (
            <Button
              variant="text"
              size="sm"
              disabled={record.isPending}
              onClick={() => {
                setValue("");
                save(null);
              }}
              data-testid="tax-completion-clear"
            >
              Clear
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
